import { NgFor, NgIf, NgClass, CommonModule } from '@angular/common';
import { AfterViewInit, Inject } from '@angular/core';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, 
    HostBinding, inject, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, 
    TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, QueryList } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormBuilder, 
    UntypedFormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocomplete, MatAutocompleteModule, 
    MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FuseAlertComponent } from '@fuse/components/alert';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FuseCardComponent } from '@fuse/components/card';
import { Alert } from 'app/models/alert';
import { CreateFolder, Asset, AssetPathItem } from 'app/models/document';
import { Util } from 'app/models/util';
import { DocumentService } from 'app/services/document.service';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { FileManagerListComponent } from '../../list/list.component';
import { MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar'; 
import { EventService } from 'app/services/event.service';
import { ToastrService } from 'ngx-toastr';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { MatRippleModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AssetsSearch, ItemPagination } from '../../file-manager.types';
import { fuseAnimations } from '@fuse/animations/public-api';
import { error } from 'console';
import { NgxLoadingModule } from 'ngx-loading';
import { MatButtonVariantDirective } from '@fuse/directives/mat-button-variant.directive';
import { FullNamePipe } from '@fuse/pipes/full-name.pipe';

@Component({
    selector     : 'modal-move',
    templateUrl  : './move.component.html',
    styleUrls    : ['./move.component.scss'],
    animations: [
        fuseAnimations,
        trigger('listAnimation', [
          transition('* => *', [ // Applies the animation upon any change in state
            query(':enter', [ // Targets newly inserted elements
              style({ opacity: 0, transform: 'scale(0.5)' }), // Initial style before animation starts
              stagger('300ms', [ // Staggers the animations of each element by 100ms
                animate('300ms ease-out', style({ opacity: 1, transform: 'scale(1)' })), // Final style after animation
              ]),
            ], { optional: true }), // Marks the query as optional, which is useful for when there are no items
          ]),
        ]),
        trigger('textAnimation', [
            transition(':enter', [
              style({ opacity: 0, transform: 'translateX(100%)' }),
              animate('0.5s 500ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
            ]),
            transition(':leave', [
              animate('0.25s ease-in', style({ opacity: 0, transform: 'translateX(-100%)' })),
            ]),
        ]),
        trigger('messagesAnimation', [
            transition('* => *', [ // This transition applies to any change in the list
              query(':enter', [ // :enter is a special selector for items being added to the DOM
                style({ opacity: 0, transform: 'translateY(30px)' }), // Initial state
                stagger(100, [ // Staggered effect, with 100ms delay between each item
                  animate('0.5s ease-out', style({ opacity: 1, transform: 'translateY(0)' })), // Final state
                ]),
              ], { optional: true }), // 'optional: true' is used because Angular throws an error if no elements are found with the :enter selector
            ]),
        ]),
      ],
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports: [
        MatButtonModule,
        MatIconModule,
        FormsModule,
        FileManagerListComponent,
        QuillModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        NgIf,
        NgFor,
        CommonModule,
        QuillEditorComponent,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        FuseCardComponent,
        FuseAlertComponent,
        NgIf, 
        NgClass,
        MatButtonModule, 
        MatIconModule, 
        FormsModule, 
        MatAutocompleteModule, 
        ReactiveFormsModule, 
        MatFormFieldModule, 
        MatInputModule, 
        MatTooltipModule,
        ScrollingModule,
        FuseScrollbarDirective,
        MatRippleModule,
        NgFor,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatCheckboxModule,
        NgxLoadingModule,
        MatButtonVariantDirective,
        FullNamePipe
    ],
})
export class MoveComponent implements OnInit, AfterViewInit
{
    @ViewChild(FileManagerListComponent, { static: true }) public fileList : FileManagerListComponent;
    @ViewChild('hiddenInput') hiddenInput: ElementRef;
    moveItemForm: UntypedFormGroup;
    folder : CreateFolder = new CreateFolder();
    alert : Alert = new Alert();

    showAlert : boolean = false;
    selectedDestination:  {id:string,name:string}= {id:'',name:''};
    opened: boolean = false;
    isLoading:boolean = false
    inputValue: string = '';
    resultSets: any[];
    searchControl: UntypedFormControl = new UntypedFormControl();
    private _matAutocomplete: MatAutocomplete;
    asset : Asset = null;
    assets:Asset[]=[]

    folders: any[]=[];
    selectedFolder:Asset= null;
    rootId:string=this.util.getRootFromSession()
    parentFolderId:string=null
    page:ItemPagination=new ItemPagination()
    assetSearch:AssetsSearch = new AssetsSearch();
    paths: AssetPathItem[] = [];
    private searchInterval = null;
    showSpinner:boolean = false;
    searchedResults = [];
    multiItems:boolean = false;
    isWebComponent:boolean = this.util.getIsWebApp()
    folderListScrollbarOptions = { wheelPropagation: true, suppressScrollX: true, };

    /**
     * Constructor
     */
    constructor(
        public matDialogRef: MatDialogRef<MoveComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private _formBuilder: UntypedFormBuilder,
        public docService : DocumentService,
        public util : Util,
        public  eventSer : EventService,
        private toastr : ToastrService,
        private _matDialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
    )
    {
    }
    ngAfterViewInit(): void {
        this.hiddenInput.nativeElement.focus();
    }

        /**
     * Setter for bar search input
     *
     * @param value
     */
        @ViewChild('barSearchInput')
        set barSearchInput(value: ElementRef) {
            // If the value exists, it means that the search input
            // is now in the DOM, and we can focus on the input..
            if ( value ) {
                // Give Angular time to complete the change detection cycle
                setTimeout(() => {
                    // Focus to the input element
                    //value.nativeElement.focus();
                });
            }
        }
        
      /**
     * Setter for mat-autocomplete element reference
     *
     * @param value
     */
      @ViewChild('matAutocomplete')
      set matAutocomplete(value: MatAutocomplete) {
          this._matAutocomplete = value;
      }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
      
        // Create the form
        this.moveItemForm = this._formBuilder.group({
           folderName: ['', [Validators.required]],
           description   : [''],
        });
        if (!Array.isArray(this.data)) {
            this.initConfig()
            this.updateTargetView()
        }
        if(Array.isArray(this.data)){
           this.initMultiConfig()
           this.updateTargetView()
        }
 
    }

    /**
     * Discard the action
     */
    cancel(): void {
        this.matDialogRef.close();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    
    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void {   
        // Escape
        if ( event.code === 'Escape' ) {
            // If the appearance is 'bar' and the mat-autocomplete is not open, close the search
            if (!this._matAutocomplete.isOpen ) {
                this.close();
            }
        }
    }
    /**
     * Open the search
     * Used in 'bar'
     */
    open(): void {
        // Return if it's already opened
        if ( this.opened ) {
            return;
        }

        // Open the search
        this.opened = true;
    }

    /**
     * Close the search
     * * Used in 'bar'
     */
    close(): void {
        // Return if it's already closed
        if ( !this.opened ) {
            return;
        }

        // Clear the search input
        this.searchControl.setValue('');
        // Close the search
        this.opened = false;
    }
    drillDown(folder: Asset): void {
        this.parentFolderId = folder.id
        this.setDestination(folder)
        this.updateTargetView()
        //update view to show the selected folder and its children subfolders
    }

    getFolders(){
        this.assetSearch.parent = this.parentFolderId
        this.isLoading = true
        this.docService.getAssets(null,this.assetSearch).then(res=>{
            const folders = this.docService.getData(res)
            this.folders = folders
            this.isLoading = false
        }).catch(error=>{
            this.isLoading = false
        })
    }

    getLocation(){
        this.docService.getAssetsPath(this.parentFolderId).then(res=>{
            this.paths=this.docService.getData(res)
            if(this.isWebComponent){
                const rootIndex = this.rootId ? this.paths.findIndex((path) => path.id === this.rootId) : -1;
                if (rootIndex > 0) this.paths.splice(0, rootIndex);
            }
        }).catch(error=>{
            console.log(error)
        })
    }

    goOneLevelUp(){
        const asset:Asset=new Asset()
        const nextParent=this.paths[this.paths.length-2]

        asset.id = nextParent.id
        asset.name = nextParent.name
        this.setDestination(asset)

        this.parentFolderId=this.paths[this.paths.length-2].id
        
        this.assetSearch.parent=this.parentFolderId
        
        this.isLoading=true
        this.docService.getAssets(null,this.assetSearch).then(res=>{
            this.folders = this.docService.getData(res)

            this.isLoading=false
            this.getLocation()
        }).catch(error=>{
            console.log(error)
            this.isLoading=false
        })
    }

    configSearch(){
        this.assetSearch.parent = this.rootId
        this.assetSearch.fileType = 'FOLDER'
        this.assetSearch.sortColumn = 'NAME'
        this.assetSearch.sortDirection = 'ASCENDING'
    }

    updateTargetView(){
        this.getFolders()
        this.getLocation()
    }

    Navigate(path:{id:string,name:string}){
        this.parentFolderId = path.id

        const asset = new Asset()
        asset.id = path.id
        asset.name = path.name
        this.setDestination(asset)
        this.updateTargetView()
    }

       /**
     * Opens a dialog to move the specified item.
     * @param item The item to be moved.
     */
       public moveItemDialog() {
        let sourceMessage=""
        let sourceId_s=null

        if(this.multiItems){
            sourceId_s=this.data.map(asset=>asset.id)
            sourceMessage = "selected '<b>files/folders</b>'"
        }
        else
        {
            sourceId_s=this.data.id
            sourceMessage = `'<b>${this.data.name}'</b>`
        }

        const destination = this.selectedDestination;
        
        const message=`You are about to move ${sourceMessage} to "<b>${destination.name}</b>". <br><br> <span class="font-medium">Are you sure?</span>`
        const defaultConfig = this.util.moveForm;
        const fullConfig = {...defaultConfig, message};
        const builder = this._formBuilder.group(fullConfig).value;

        const dialogRef = this._fuseConfirmationService.open(builder);
        
        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.docService.moveAsset(sourceId_s, destination.id).then(res=>{
                    this.matDialogRef.close()
                    this.eventSer.getData.emit(true)
                    this.toastr.success("Moved Successfully")
                }).catch(error=>{
                    console.log(error)
                });
            }
        });
    }

        /**
     * Opens a dialog to move the specified item.
     * @param item The item to be moved.
     */
        public CopyItemDialog() {
            let sourceMessage = ""
            let sourceId_s = null

            if (this.multiItems) {
                sourceId_s = this.data.map(asset => asset.id)
                sourceMessage = "selected '<b>files/folders</b>'"
            }
            else {
                sourceId_s = [this.data.id]
                sourceMessage = `'<b>${this.data.name}'</b>`
            }

            const source:string=this.data
            const destination=this.selectedDestination
            let message=`You are about to copy ${sourceMessage} to "<b>${destination.name}</b>". <br><br> <span class="font-medium">Are you sure?</span>`
    
            const dialogRef = this._fuseConfirmationService.open({message:message,icon:{color:'primary',name:'feather:move'}});
            
            // Subscribe to afterClosed from the dialog reference
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    this.isLoading = true;
                    this.docService.copyAsset(sourceId_s, destination.id).then(res=>{
                        this.matDialogRef.close()
                        this.eventSer.getData.emit(true)
                        this.isLoading = false;
                        this.toastr.success("Copied Successfully")
                    }).catch(error=>{
                        this.isLoading = false;
                        console.log(error)
                    });  
                }
            });
        }

    initConfig(){
        this.configSearch()
        this.parentFolderId = this.rootId
        const targetFolder:Asset = new Asset()
        targetFolder.id = this.rootId
        targetFolder.name = "Drive"
        this.setDestination(targetFolder)
    }

    setDestination(asset:Asset){
        this.selectedDestination.id=asset.id
        this.selectedDestination.name=asset.name
    }

    public onValueChanged(event) {
        let val = event ?? ""
      
        clearTimeout(this.searchInterval);
        this.showSpinner = false;
        if (!this.util.isValidated(val?.trim()) || val.length < 3) return;
        this.assets = [];
        this.searchInterval = setTimeout(() => {
        this.searchAssets(val?.trim());
        }, 500);
      }

      private searchAssets(val:string) {
        this.showSpinner = true;
        const assetSearch:AssetsSearch = new AssetsSearch()
        assetSearch.keywords = val
        assetSearch.parent = this.isWebComponent ? this.rootId : null
        assetSearch.assetType = 'FOLDER'
        this.docService.getAssets(null,assetSearch).then(res=>{
             this.assets=this.docService.getData(res)
        })
      }

      initMultiConfig(){
        this.multiItems=true
        this.configSearch()
        this.parentFolderId = this.rootId
        const targetFolder:Asset = new Asset()
        targetFolder.id = this.rootId
        targetFolder.name = "Drive"
        this.setDestination(targetFolder)
    }
}
